import { z } from 'zod';

// src/cordata/AccessibleContentFeature.ts
var LanguageMapSchema = z.object({
  nb: z.string(),
  nn: z.string()
});
var LanguageMapWithEnglishSchema = z.object({
  nb: z.string(),
  nn: z.string(),
  en: z.string()
});
var LanguageMapListSchema = z.object({
  nb: z.array(z.string()),
  nn: z.array(z.string())
});
var LanguageMapListWithEnglishSchema = z.object({
  nb: z.array(z.string()),
  nn: z.array(z.string()),
  en: z.array(z.string())
});

// src/cordata/AccessibleContentFeature.ts
var AccessibleContentFeatureSchema = z.object({
  type: z.literal("AccessibleContentFeature"),
  id: z.string(),
  code: z.union([z.literal("ta"), z.literal("tb")]),
  label: LanguageMapSchema,
  nortargetUri: z.string()
}).strict();
var AgeGroupSchema = z.object({
  type: z.literal("AgeGroup"),
  id: z.string(),
  code: z.string(),
  plasCode: z.enum(["child", "youngAdult", "adult"]).optional(),
  marc21Code: z.string().optional(),
  name: LanguageMapSchema,
  nortargetUri: z.string().optional()
}).strict();
var AgeLimitSchema = z.object({
  type: z.enum(["PegiAgeLimit", "NmaAgeLimit"]),
  id: z.string(),
  years: z.number(),
  uri: z.string().url()
}).strict();
var originNames = ["promus", "catalogueHarvester", "filmoteket", "mediastore", "nmbf"];
var librarySystems = ["bibliofil", "cicero", "quria"];
var isLibrarySystem = (item) => typeof item === "string" && librarySystems.includes(item);
var promusIdItemNamespace = "item";
var promusIdAuthorityNamespace = "authority";
var nmbfIdNamespace = "nmbf";
var originSchema = z.union([
  z.object({ name: z.literal("promus"), id: z.string() }),
  z.object({ name: z.literal("filmoteket"), id: z.string() }),
  z.object({ name: z.literal("nmbf"), id: z.string() }),
  z.object({
    name: z.literal("catalogueHarvester"),
    librarySystem: z.enum(librarySystems),
    catalogue: z.string(),
    id: z.string()
  })
]);
var originLabelMap = {
  promus: "Promus",
  filmoteket: "Filmoteket",
  catalogueHarvester: "Kataloger",
  mediastore: "Mediebr\xF8nnen",
  nmbf: "Norsk musikkbibliotekforening"
};
var cordataTransformWarnings = {
  W0001: "Mangler data fra Promus-tabell som ikke er mappet enda.",
  W0002: "Mangler data fra Promus-referanse som ikke kunne mappes.",
  // No longer used: "W0003": "Uttrykksgruppe inneholder mer enn én tittel.",
  W0004: "Oversetter eller innleser for manifestasjon er avledet fra annen manifestasjon per MR55",
  // No longer used: "W0005": "Ulike manifestasjoner av uttrykket har ulike verdier for et felt",
  W0007: "Ugyldig AuthorityWork-rad: mangler tittel",
  W0008: "Rad(er) i AuthorityTitle med PartOf_TitleID ble ignorert fordi en Collection i Cordata maks kan ha \xE9n superCollection.",
  W0009: "Tvetydig 260 og 264-felt: Mer enn ett utgivelsesssted ($a) og f\xE6rre utgivelsessteder enn utgivere ($b)",
  W0010: "Katalogposten manglet 260 og 264-felt.",
  W0011: "Katalogpostens 260- eller 264-felt manglet $b.",
  W0012: "Fant ingen manifestasjonstittel (b\xE5de 245 og 240 manglet).",
  W0013: "Dokumenttype er usikker fordi katalogposten manglet 019$b og 020$a (ISBN).",
  W0015: "Ulike verdier for beskjeftigelse (PersonOccupation) ble funnet for person",
  W0016: "Ignorerte AuthorityPerson-raden ${primaryKey} fordi den manglet Felles_ID",
  W0017: "Mapping av analytter (AuthorityPerson.TopicTitle) er ikke implementert enda.",
  W0018: "Ignorerte ufullstendig verksreferanse (101/701-felt) fra manifestasjonen fordi feltet manglet Authority_ID.",
  W0019: "Ignorerte manifestasjonspost fordi spr\xE5k ikke kunne bestemmes for denne.",
  W0020: "Brukte standard uttrykkstype for manifestasjon fordi vi ikke hadde noen mapping fra den angitte dokumenttypen",
  W0021: "Spilletid for filmen kunne ikke bestemmes fordi 300$a manglet",
  W0022: "Spilletid for filmen kunne ikke bestemmes fordi 300$a hadde et ukjent format",
  W0023: "Mapping av Promus-rad f\xF8rte ikke til noen Cordata-entiteter. Sjekk om det finnes mer detaljerte advarsler.",
  W0024: "Kunne ikke konvertere Bibliofil-post fordi den manglet felles Bibliofil-ID",
  W0025: "Kunne ikke konvertere MARC-post fordi den hadde et ukjent format",
  W0026: "Ugyldig spr\xE5kkode i 041$b/j (undertekster for dataspill)",
  W0027: "Ugyldig spr\xE5kkode i 041$b/j (undertekster for film)",
  W0028: "Ugyldig spr\xE5kkode i 041$p (undertekster for h\xF8rselshemmede)",
  W0029: "Ugyldig spr\xE5kkode i 041$q (synstolkning)",
  W0030: "Ugyldig spr\xE5kkode i 008 eller 041$a (grunnlag for uttrykkspr\xE5k).",
  W0031: "Ugyldig spr\xE5kkode i 101 eller 701 $l",
  W0032: "Spr\xE5k for verksvarianttittel kunne ikke mappes p\xE5 grunn av ugyldig spr\xE5kkode i AuthorityWork.FirstLanguage",
  W0033: "Ignorerte ugyldig rad i AuthorityWork fordi den manglet 'TitlePreferred'",
  W0034: "Ignorerte AuthorityCorp-rad fordi den manglet Felles_ID.",
  W0035: "Mapping av analytter (AuthorityCorp.TopicTitle) er ikke implementert enda.",
  W0036: "Mapping av jurisdiksjonsnavn fra AuthorityCorp er ikke implementert enda.",
  W0038: "Rad i EnumAgeLimit ble ignorert fordi den hadde ugyldig \xE5rsgrense",
  W0039: "Fant ulike aldersgrenser assosiert med samme verk.",
  W0040: "Fant aldersgrense p\xE5 verk som ikke er film eller dataspill.",
  W0041: "Kunne ikke konvertere Quria-post fordi den manglet 001/003.",
  W0042: "Klarte ikke bestemme dokumenttype. Verdiene i 336,337,338 tilsier 'film (plate)', men 300$a er tomt, s\xE5 vi f\xE5r ikke bestemt medium.",
  W0043: "Klarte ikke bestemme dokumenttype. Verdiene i 336, 337 og 338 tilsier 'film (plate)' men 300$a feltet matcher ikke noen av mediumene vi s\xF8ker etter.",
  W0044: "Klarte ikke bestemme dokumenttype. Verdiene i 336,337,338 tilsier 'musikk (plate)', men 300$a er tomt, s\xE5 vi f\xE5r ikke bestemt medium.",
  W0045: "Klarte ikke bestemme dokumenttype. Verdiene i 336, 337 og 338 tilsier 'musikk (plate)' men 300$a feltet matcher ikke noen av mediumene vi s\xF8ker etter.",
  W0046: "Klarte ikke bestemme dokumenttype. Verdiene i 336,337,338 tilsier 'digital lydbok', men 300$a er tomt, s\xE5 vi f\xE5r ikke bestemt medium.",
  W0047: "Klarte ikke bestemme dokumenttype. Verdiene i 336, 337 og 338 tilsier 'lydbok (digital)' men 300$a feltet matcher ikke noen av mediumene vi s\xF8ker etter",
  W0048: "Klarte ikke bestemme dokumenttype. Verdiene i 336,337,338 tilsier 'dataprogram (plate)', men 300$a er tomt, s\xE5 vi f\xE5r ikke bestemt medium.",
  W0049: "Klarte ikke bestemme dokumenttype. Verdiene i 336, 337 og 338 tilsier 'dataprogram (plate)' men 300$a feltet matcher ikke noen av mediumene vi s\xF8ker etter",
  W0050: "Formatet for dokumenttypen dataprogram (uspesifisert) kan ikke bestemmes fordi 300$a, 245$b og 250$a feltene er tomme.",
  W0051: "Klarte ikke bestemme dokumenttype. Verdiene i 336, 337 og 338 tilsier 'dataprogram (uspesifisert)' men 300$a feltet matcher ikke noen av mediumene vi s\xF8ker etter.",
  W0052: "Klarte ikke bestemme dokumenttype. Verdiene i 336,337,338 tilsier 'dataprogram (lagringsbrikke)', men 650$a er tomt, s\xE5 vi f\xE5r ikke bestemt medium.",
  W0053: "Formatet for dokumenttypen dataprogram (lagringsbrikke) kan ikke bestemmes fordi 650$a feltet matcher ikke med de predefinerte formatene.",
  W0054: "Klarte ikke \xE5 bestemme dokumenttype fordi verdiene i 336,337,338 er ukjente.",
  W0055: "Klarte ikke mappe rolle, ugyldig verdi i 1XX/7XX $e/$4.",
  W0056: "Navn er merket som p\xE5 invertert form, men klarte ikke \xE5 avinvertere det.",
  W0057: "Mapping fra MARC-felt XXX er ikke implementert enda.",
  W0058: "Ulike manifestasjoner av verket hadde ulik aldersgruppemerkerking (bemuing).",
  W0059: "Ulike manifestasjoner av verket hadde ulike form/sjanger-termer.",
  W0060: "Ulike manifestasjoner av verket hadde ulike emneord.",
  W0061: "Manifestasjonen ble tilknyttet et uttrykk med bidragsyter(e) som ikke eksplisitt var oppgitt p\xE5 den opprinnelige manifestasjonen (MR55).",
  W0062: "Ulike manifestasjoner av verket hadde ulike verdier for skj\xF8nn/fag (isFiction)",
  W0063: "Aldersgruppekoden i 008/22 kunne ikke mappes.",
  W0064: "Aldersgruppekoden i 019$a kunne ikke mappes.",
  W0065: "Ukjent opprinnelsesland i Filmoteket-posten.",
  W0066: "Ukjent spr\xE5k i Filmoteket-posten.",
  W0067: "Sl\xE5r sammen manifestasjonene med varenummer XXX og id XXX",
  W0068: "Sl\xE5r sammen manifestasjoner med ulike utgaver XXX",
  W0069: "Sl\xE5r sammen manifestasjoner med ulike publikasjons\xE5r XXX",
  W0070: "Ukjent rolle i Filmoteket-posten.",
  W0071: "Ukjent sjanger i Filmoteket-posten.",
  W0072: "Ukjent aldersgrense i Filmoteket-posten.",
  W0073: "Mangler oppsummering i Filmoteket-posten.",
  W0074: "Promus: Bidragsytere er utledet fra statementOfResponsibility (245$c) for noen av manifestasjonene i uttrykket",
  W0075: "Inkonsistens mellom medium (245$h) og dokumenttype (019$b)",
  W0076: "Bruker manifestasjonstittel som stedfortreder for manglende verkstittel",
  W0077: "Utelater bidragsyter pga. manglende roller",
  W0078: "Manifestasjon opprettet fra innh\xF8stet katalogpost som opprinnelig kommer fra Bibbi. Mulig kandidat for verkifisering.",
  W0079: "Ulikt antall instrumenteringer (658) p\xE5 bokm\xE5l og nynorsk",
  W0080: "Klarte ikke \xE5 tolke instrumentering (658)",
  W0081: "Mapping fra Promus: Klarte ikke \xE5 tolke AuthorityTitle.MusicNr",
  W0082: "Ulike manifestasjoner av verket hadde ulike verdier for innhold (740$a, 700$t eller 710$t med ind: 2)",
  W0083: "Fant verken norsk eller engelsk tittel i Filmoteket-posten, bruker originaltittel",
  W0084: "Mangler spilletid fra Filmoteket-posten",
  W0085: "Kunne ikke finne sjanger i vokabular",
  W0086: "Sjanger ulik vokabular",
  W0087: "Promus: Ugyldig spr\xE5kkode i AuthorityTitle.LanguageCode (spr\xE5k for serieuttrykk)",
  W0088: "Promus: Ugyldig spr\xE5kkode i verksspr\xE5k (AuthorityWork.FirstLanguage/FirstLanguageMul)",
  W0089: "Lokal katalogpost: Ugyldig spr\xE5kkode for originalspr\xE5k i 041$h",
  W0090: "Lokal katalogpost: Ukjent verdi i LDR/17",
  W0091: "Lokal katalogpost: Bidragsyter(e) er utledet fra statementOfResponsibility",
  W0092: "Lokal katalogpost: Spr\xE5k er utledet fra statementOfResponsibility",
  W0093: "Felles: Ugyldig spr\xE5kkode for mellomspr\xE5k for oversettelser i 041$k",
  W0094: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for tittel (mainTitle)",
  W0095: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for serienummer (numberInSeries)",
  W0096: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for spillspr\xE5k (gameTextLanguages)",
  W0097: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for antall spillere (numberOfPlayers)",
  W0098: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for instrumenter (instrumentations)",
  W0099: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for parallelltekst (hasParallelText)",
  W0100: "Promus: Ulike manifestasjoner av uttrykket har ulike verdier for mellomspr\xE5k brukt i oversettelser (intermediateTranslationLanguages)",
  W0101: "Promus: Ulike manifestasjoner av verket hadde ulike 019$e-verdier (lettlest, tilrettelagt innhold)",
  W0103: "Promus: Ukjent format p\xE5 verdien i datofeltet til personautoritet",
  W0104: "Promus: Ett eller flere emnefelt lot seg ikke mappe (person som emne)",
  W0105: "Promus: Ett eller flere emnefelt lot seg ikke mappe (tittel som emne)",
  W0106: "Promus: Ett eller flere emnefelt lot seg ikke mappe (annet som emne)"
};
var CordataTransformWarningSchema = z.object({
  code: z.string(),
  info: z.string(),
  source: z.string().optional()
});

// src/cordata/CordataEntity.ts
var CordataEntitySchema = z.object({
  type: z.string(),
  id: z.string()
});
var CordataEntityV2Schema = z.object({
  type: z.string(),
  id: z.string(),
  _origin: originSchema.optional(),
  _sourceReferences: z.array(z.string()).optional(),
  _transformWarnings: z.array(CordataTransformWarningSchema).optional(),
  _signatures: z.array(z.string()).optional(),
  _cordataVersion: z.string().optional(),
  _updatedAt: z.string().optional(),
  _version: z.string().optional()
});
var isFromPromus = (entity) => entity._origin?.name === "promus";
var PlaceType = z.enum(["Country"]);

// src/cordata/Place.ts
var PlaceSchema = z.object({
  type: z.literal("Place"),
  id: z.string(),
  identifiers: z.object({
    bibbiAuthorityId: z.string().optional()
  }).strict(),
  placeType: PlaceType.optional(),
  // @TODO: Make non-optional once we can!
  name: LanguageMapSchema,
  //??
  specification: z.string().optional(),
  alternativeNames: LanguageMapListSchema,
  broaderPlace: z.lazy(() => PlaceSchema).optional(),
  definition: z.string().optional(),
  scopeNote: z.string().optional()
});

// src/cordata/Nationality.ts
var Nationality = z.object({
  type: z.literal("Nationality"),
  id: z.string(),
  demonym: z.string(),
  code: z.string(),
  scopeNote: z.string().optional(),
  country: PlaceSchema.optional(),
  countryCodes: z.object({
    iso3166_2: z.string().optional(),
    iso3166_3: z.string().optional(),
    iso3166_numeric: z.number().optional(),
    marc21: z.string().optional()
  }).strict()
});
var AgentIdentifiersSchema = z.object({
  bibbiAuthorityId: z.string().optional(),
  bokbasenId: z.string().optional(),
  isni: z.string().optional(),
  norafId: z.string().optional()
}).strict();

// src/cordata/CollectiveAgent.ts
var CollectiveAgentSchema = CordataEntityV2Schema.merge(
  z.object({
    type: z.literal("CollectiveAgent"),
    id: z.string(),
    identifiers: AgentIdentifiersSchema,
    collectiveAgentType: z.string(),
    name: LanguageMapSchema,
    alternativeNames: LanguageMapListSchema,
    nationality: z.array(Nationality),
    scopeNote: z.string().optional()
  })
);
var YearSchema = z.object({
  year: z.number(),
  isApproximate: z.boolean()
});
var PersonSchema = CordataEntityV2Schema.merge(
  z.object({
    type: z.literal("Person"),
    id: z.string(),
    identifiers: AgentIdentifiersSchema,
    name: LanguageMapSchema,
    catalogueName: z.string().optional(),
    ordinal: z.string().optional(),
    personTitle: LanguageMapSchema.optional(),
    birth: YearSchema.optional(),
    death: YearSchema.optional(),
    activeYears: YearSchema.optional(),
    unspecifiedDate: z.string().optional(),
    nationality: z.array(Nationality),
    gender: z.string().optional(),
    scopeNote: z.string().optional(),
    alternativeNames: LanguageMapListSchema,
    fieldOfActivity: LanguageMapSchema.optional()
  })
);

// src/cordata/Agent.ts
var AgentSchema = z.discriminatedUnion("type", [
  PersonSchema,
  CollectiveAgentSchema
]);
var BindingTypeSchema = z.object({
  type: z.literal("BindingType"),
  id: z.string(),
  code: z.string(),
  name: LanguageMapSchema,
  onixCodes: z.string().array().optional()
}).strict();
var CollectionExpressionEntrySchema = z.object({
  collection: z.lazy(() => CollectionExpressionSchema),
  numberInSeries: z.string().optional()
});

// src/vocabularies/languages.ts
var languageNameMap = {
  abk: "Abkhasisk",
  ach: "Acholi",
  afr: "Afrikaans",
  akk: "Akkadisk",
  alb: "Albansk",
  amh: "Amharisk",
  ang: "Angelsaksisk",
  ara: "Arabisk",
  arc: "Arameisk",
  arm: "Armensk",
  aze: "Aserbajdsjansk",
  bam: "Bambara",
  baq: "Baskisk",
  bel: "Hviterussisk",
  bem: "Bemba",
  ben: "Bengali",
  bis: "Bislama",
  bnt: "Bantuspr\xE5k",
  bos: "Bosnisk",
  bre: "Bretonsk",
  bul: "Bulgarsk",
  bur: "Burmesisk",
  byn: "Bilin",
  cat: "Katalansk",
  ceb: "Cebuano",
  che: "Tsjetsjensk",
  chi: "Kinesisk",
  chu: "Kirkeslavisk",
  cic: "Chickasaw",
  ckb: "Sorani (S\xF8rkurdisk)",
  cmn: "Mandarinkinesisk",
  cnr: "Montenegrinsk",
  cop: "Koptisk",
  cze: "Tsjekkisk",
  dan: "Dansk",
  dut: "Nederlandsk",
  egy: "Egyptisk",
  eng: "Engelsk",
  enm: "Middelengelsk",
  epo: "Esperanto",
  est: "Estisk",
  ewe: "Ewe",
  fao: "F\xE6r\xF8ysk",
  fat: "Fante",
  fij: "Fijiansk",
  fin: "Finsk",
  fiu: "Finsk-ugrisk",
  fkv: "Kvensk",
  fre: "Fransk",
  frm: "Mellomfransk",
  fro: "Gammelfransk",
  fry: "Frisisk",
  ful: "Fulfulde",
  gaa: "Ga",
  geo: "Georgisk",
  ger: "Tysk",
  gez: "Etiopisk",
  gla: "G\xE6lisk",
  gle: "Irsk",
  glg: "Galegisk/Galisisk",
  gmh: "Middelh\xF8ytysk",
  got: "Gotisk",
  grc: "Klassisk gresk",
  gre: "Gresk",
  guj: "Gujarati",
  hau: "Hausa",
  heb: "Hebraisk",
  hil: "Hiligaynon",
  hin: "Hindi",
  hrv: "Kroatisk",
  hun: "Ungarsk",
  ibo: "Ibo",
  ice: "Islandsk",
  iku: "Inuktitut",
  ilo: "Iloko",
  ind: "Indonesisk",
  ipk: "Inupiak",
  ira: "Iranske spr\xE5k",
  ita: "Italiensk",
  jpn: "Japansk",
  kal: "Kalaalisut (Gr\xF8nlandsk)",
  kan: "Kannada",
  kaz: "Kasakhisk",
  khm: "Khmer",
  kho: "Khotanesisk",
  kik: "Kikuyu",
  kin: "Kinyarwanda",
  kmr: "Kurmanji (Nordkurdisk)",
  kon: "Kongo",
  kor: "Koreansk",
  kua: "Kwanyama",
  kur: "Kurdisk",
  lao: "Lao (Laotisk)",
  lat: "Latin",
  lav: "Latvisk",
  lin: "Lingalsk",
  lit: "Litauisk",
  lkt: "Lakota",
  lug: "Luganda",
  mac: "Makedonsk",
  mao: "Maori",
  mar: "Marathi",
  may: "Malayisk",
  mlg: "Madagassisk",
  mlt: "Maltesisk",
  mnk: "Mandinka",
  mol: "Moldavisk",
  mon: "Mongolsk",
  myn: "Maya",
  nds: "Nedertysk",
  nep: "Nepali",
  nic: "Niger-Kongospr\xE5k",
  nno: "Norsk (nynorsk)",
  nob: "Norsk (bokm\xE5l)",
  nom: "Mellomnorsk",
  non: "Gammelnorsk",
  nor: "Norsk",
  nso: "Nordsotho",
  oci: "Proven\xE7alsk",
  orm: "Oromo",
  pan: "Panjabi",
  per: "Farsi",
  pli: "Pali",
  pol: "Polsk",
  por: "Portugisisk",
  pra: "Prakrit",
  pro: "Proven\xE7alsk til 1500",
  prs: "Dari",
  pus: "Pashto",
  roh: "Retoromansk",
  rom: "Romani",
  rum: "Rumensk",
  run: "Kirundi",
  rus: "Russisk",
  sah: "Jakutisk",
  san: "Sanskrit",
  sat: "Santali",
  sco: "Skotsk",
  sgn: "Tegnspr\xE5k",
  sin: "Singalesisk",
  sit: "Sino-tibetansk",
  sjd: "Kildinsamisk",
  sje: "Pitesamisk",
  sjk: "Kemisamisk",
  sjt: "Tersamisk",
  sju: "Umesamisk",
  slo: "Slovakisk",
  slv: "Slovensk",
  sma: "S\xF8rsamisk",
  sme: "Nordsamisk",
  smi: "Andre samiske spr\xE5k",
  smj: "Lulesamisk",
  smn: "Inarisamisk",
  smo: "Samoansk",
  sms: "Skoltesamisk",
  som: "Somali",
  sot: "Sesotho",
  spa: "Spansk",
  srp: "Serbisk",
  sux: "Sumerisk",
  swa: "Swahili",
  swe: "Svensk",
  syr: "Syrisk",
  tam: "Tamil",
  tay: "Atayal",
  tel: "Telugu",
  tgl: "Tagalog",
  tha: "Thai",
  tib: "Tibetansk",
  tir: "Tigrinja",
  tkl: "Tokelaisk",
  tmh: "Tuareg",
  ton: "Tongansk",
  tur: "Tyrkisk",
  ukr: "Ukrainsk",
  urd: "Urdu",
  uzb: "Usbekisk",
  vie: "Vietnamesisk",
  wel: "Walisisk",
  wen: "Sorbiske spr\xE5k",
  wol: "Wolof",
  xho: "Xhosa",
  yid: "Jiddisk",
  yor: "Yoruba",
  ypk: "Yupikspr\xE5k",
  yue: "Kantonesisk (Yue)",
  zul: "Zulu",
  // Special codes
  und: "Ukjent spr\xE5k",
  zxx: "Uten spr\xE5klig innhold"
};
var isCordataLanguageCode = (code) => code in languageNameMap;

// src/cordata/Language.ts
var languageCodes = Object.keys(languageNameMap);
var languageNames = Object.values(languageNameMap);
var LanguageSchema = z.object({
  code: z.enum(languageCodes),
  name: z.enum(languageNames)
}).strict();

// src/cordata/Title.ts
var TitleSchema = z.object({
  mainTitle: z.string(),
  subtitle: z.string().optional(),
  partNumber: z.string().optional(),
  partTitle: z.string().optional(),
  titleLanguage: LanguageSchema.optional(),
  titleType: z.enum(["norwegian_cinema_title", "other_variant_title", "cover_title", "added_title_page_title", "spine_title"]).optional()
}).strict();
var RoleSchema = z.object({
  type: z.literal("Role"),
  id: z.string(),
  code: z.string(),
  label: LanguageMapSchema,
  marc21Code: z.string().optional(),
  onixCodes: z.string().array().optional(),
  normarcCode: z.string().optional(),
  bibframeRelator: z.string().optional(),
  rdaRelator: z.string().optional()
}).strict();

// src/cordata/Contributor.ts
var ContributorSchema = z.object({
  // key: z.string(),
  agent: AgentSchema,
  roles: z.array(RoleSchema),
  isMainContributor: z.boolean()
}).strict();

// src/cordata/CollectionWork.ts
var CollectionWorkSchema = z.object({
  type: z.literal("CollectionWork"),
  collectionType: z.enum(["series", "fictional_universe"]),
  id: z.string(),
  contributors: z.array(ContributorSchema),
  title: TitleSchema
}).strict();

// src/cordata/CollectionExpression.ts
var CollectionExpressionSchema = z.object({
  type: z.literal("CollectionExpression"),
  id: z.string(),
  identifiers: z.object({
    bibbiAuthorityId: z.string().optional()
  }).strict(),
  title: TitleSchema,
  variantTitles: z.array(TitleSchema),
  languages: LanguageSchema.array(),
  note: z.string().optional(),
  issn: z.string().optional(),
  created: z.string().optional(),
  modified: z.string().optional(),
  superCollection: CollectionExpressionEntrySchema.optional(),
  workExpressed: CollectionWorkSchema.optional(),
  domain: z.enum(["Expression", "Manifestation"]),
  _origin: originSchema.optional()
}).strict();
var ConceptSchema = z.object({
  type: z.literal("Concept"),
  id: z.string(),
  name: LanguageMapSchema,
  alternativeNames: LanguageMapListSchema,
  identifiers: z.object({
    bibbiAuthorityId: z.string().optional()
  }).strict()
});

// src/vocabularies/workTypes.ts
var languageBasedWork = {
  type: "WorkType",
  id: "V1000",
  uri: "https://schema.nb.no/Bibliographic/Values/V1000",
  label: {
    nb: "Spr\xE5kbasert verk",
    nn: "Spr\xE5kbasert verk",
    en: "Language based work"
  }
};
var isLanguageBasedWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === languageBasedWork.uri);
var musicalWork = {
  type: "WorkType",
  id: "V1001",
  uri: "https://schema.nb.no/Bibliographic/Values/V1001",
  label: {
    nb: "Musikkverk",
    nn: "Musikkverk",
    en: "Musical work"
  }
};
var isMusicalWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === musicalWork.uri);
var movingImageWork = {
  type: "WorkType",
  id: "V1002",
  uri: "https://schema.nb.no/Bibliographic/Values/V1002",
  label: {
    nb: "Filmverk",
    nn: "Filmverk",
    en: "Moving image work"
  }
};
var isMovingImageWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === movingImageWork.uri);
var imageWork = {
  type: "WorkType",
  id: "V1003",
  uri: "https://schema.nb.no/Bibliographic/Values/V1003",
  label: {
    nb: "Bildeverk",
    nn: "Bildeverk",
    en: "Picture work"
  }
};
var isImageWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === imageWork.uri);
var cartographicWork = {
  type: "WorkType",
  id: "V1004",
  uri: "https://schema.nb.no/Bibliographic/Values/V1004",
  label: {
    nb: "Kartografisk verk",
    nn: "Kartografisk verk",
    en: "Cartographic work"
  }
};
var isCartographicWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === cartographicWork.uri);
var interactiveWork = {
  type: "WorkType",
  id: "V1005",
  uri: "https://schema.nb.no/Bibliographic/Values/V1005",
  label: {
    nb: "Interaktivt verk",
    nn: "Interaktivt verk",
    en: "Interactive work"
  }
};
var isInteractiveWork = ({ workTypes: workTypes2 }) => workTypes2.some(({ uri }) => uri === interactiveWork.uri);
var workTypes = [
  languageBasedWork,
  musicalWork,
  movingImageWork,
  imageWork,
  cartographicWork,
  interactiveWork
];
var defaultWorkType = languageBasedWork;
var workTypeIds = ["V1000", "V1001", "V1002", "V1003", "V1004", "V1005"];
var isWorkTypeId = (item) => typeof item === "string" && workTypeIds.includes(item);

// src/cordata/DocumentType.ts
var DocumentTypeSchema = z.object({
  type: z.literal("DocumentType"),
  id: z.string(),
  code: z.string(),
  format: z.string(),
  medium: z.string().optional(),
  defaultWorkTypeId: z.enum(workTypeIds).optional(),
  allowedWorkTypeIds: z.enum(workTypeIds).array().optional(),
  onixCodes: z.string().array().optional()
});
var ImprintSchema = z.object({
  type: z.literal("Imprint"),
  id: z.string(),
  identifiers: z.object({
    bokbasenId: z.string().optional()
  }).strict(),
  name: z.string()
}).strict();

// src/cordata/PublicationStatement.ts
var PublicationStatementSchema = z.object({
  imprint: ImprintSchema,
  places: z.array(z.string())
});
var LibraryCatalogueRecordReferenceSchema = z.object({
  catalogueId: z.string(),
  localRecordId: z.string(),
  libraryIds: z.array(z.string()).optional(),
  recordCreationDate: z.string().optional(),
  recordModificationDateTime: z.string().optional()
}).strict();
var NoteSchema = z.object({
  value: z.string(),
  noteType: z.enum([
    "general_note",
    "bibliographic_history_note",
    "contents_note",
    "participant_or_performer_note",
    "system_details_note",
    "language_note",
    "other_identifier_note",
    "variant_title_note",
    "title_source_note",
    "original_title_note"
  ])
});

// src/cordata/Manifestation.ts
var encodingLevelEnum = ["full", "preliminary"];
var DurationRecordSchema = z.object({
  approximate: z.boolean(),
  hours: z.coerce.number().optional(),
  minutes: z.coerce.number()
});
var ManifestationSchema = CordataEntityV2Schema.merge(
  z.object({
    type: z.literal("Manifestation"),
    id: z.string(),
    identifiers: z.record(
      z.enum(["bibbiId", "bibliofilId", "bibsentSku", "bokbasenId", "filmoteketId", "imdbId"]),
      z.string()
    ),
    createDate: z.string().optional(),
    approveDate: z.string().optional(),
    cataloguingDate: z.string().optional(),
    updateDate: z.string().optional(),
    biographicContent: z.string().optional(),
    title: TitleSchema,
    parallelTitles: z.array(TitleSchema),
    variantTitles: z.array(TitleSchema),
    coverImage: z.string().optional(),
    binding: BindingTypeSchema.optional(),
    documentType: DocumentTypeSchema.optional(),
    statementOfResponsibility: z.string().optional(),
    isbn: z.array(z.string()),
    incorrectIsbn: z.array(z.string()),
    ismn: z.array(z.string()),
    ean: z.array(z.string()),
    publicationYear: z.string().optional(),
    publicationStatements: z.array(PublicationStatementSchema),
    edition: z.string().optional(),
    contributors: z.array(ContributorSchema),
    summary: z.string().optional(),
    subtitleLanguages: z.array(LanguageSchema).optional(),
    captionLanguages: z.array(LanguageSchema).optional(),
    audioDescriptionLanguages: z.array(LanguageSchema).optional(),
    duration: DurationRecordSchema.optional(),
    collections: z.array(CollectionExpressionEntrySchema),
    libraryCatalogueRecords: z.array(LibraryCatalogueRecordReferenceSchema).optional(),
    isilCodes: z.array(z.string()).optional(),
    extent_unstable: z.string().optional(),
    accompanyingMaterial: z.string().optional(),
    kulturfond: z.object({ status: z.boolean() }).optional(),
    notes: z.array(NoteSchema).optional(),
    encodingLevel: z.enum(encodingLevelEnum).optional(),
    expressions: z.array(
      z.object({
        expressionId: z.string(),
        workId: z.string(),
        relation: z.enum(["embodies", "contains"])
      })
    ).optional()
  })
).strict();
var NumberOfPlayersSchema = z.object({
  local: z.object({ minimum: z.number(), maximum: z.number() }).optional(),
  online: z.object({ minimum: z.number(), maximum: z.number() }).optional()
}).strict();
var PlatformSchema = z.object({
  type: z.literal("Platform"),
  id: z.string(),
  code: z.string(),
  name: z.string(),
  abbreviatedName: z.string().optional()
}).strict();
var expressionTypes = [
  { code: "a", label: "Tekstlig uttrykk" },
  { code: "c", label: "Notetrykk" },
  { code: "e", label: "Kartografisk uttrykk" },
  { code: "g", label: "Film og tv-serier" },
  { code: "i", label: "Lyd (lydb\xF8ker og lydopptak)" },
  { code: "j", label: "Musikkopptak" },
  { code: "m", label: "Dataprogram" },
  { code: "o", label: "Kombidokument (ikke egentlig et uttrykk}, sorteres senere)" },
  { code: "r", label: "Realia" }
];
var expressionTypeSchema = z.union([
  z.object({ code: z.literal("a"), label: z.literal("Tekstlig uttrykk") }),
  z.object({ code: z.literal("c"), label: z.literal("Notetrykk") }),
  z.object({ code: z.literal("e"), label: z.literal("Kartografisk uttrykk") }),
  z.object({ code: z.literal("g"), label: z.literal("Film og tv-serier") }),
  z.object({ code: z.literal("i"), label: z.literal("Lyd (lydb\xF8ker og lydopptak)") }),
  z.object({ code: z.literal("j"), label: z.literal("Musikkopptak") }),
  z.object({ code: z.literal("m"), label: z.literal("Dataprogram") }),
  z.object({ code: z.literal("o"), label: z.literal("Kombidokument (ikke egentlig et uttrykk}, sorteres senere)") }),
  z.object({ code: z.literal("r"), label: z.literal("Realia") })
]);
var isExpressionTypeCode = (item) => typeof item === "string" && expressionTypes.some(({ code }) => code === item);
var expressionTypeFromCode = (code) => expressionTypes.find((expressionType) => expressionType.code === code);
var getExpressionTypeLabel = (code) => expressionTypeFromCode(code).label;
var musicInstrumentSchema = z.object({
  type: z.literal("MusicInstrument"),
  id: z.string(),
  name: LanguageMapSchema,
  alternativeNames: LanguageMapListSchema.optional()
}).strict();
var isMusicInstrument = (item) => musicInstrumentSchema.safeParse(item).success;

// src/cordata/Instrumentation.ts
var instrumentationSchema = z.object({
  type: z.literal("Instrumentation"),
  instruments: z.array(
    z.object({
      instrument: musicInstrumentSchema,
      numberOfPerformers: z.number().optional()
    })
  )
}).strict();
var isInstrumentation = (item) => instrumentationSchema.safeParse(item).success;

// src/cordata/Expression.ts
var ExpressionSchema = CordataEntityV2Schema.merge(
  z.object({
    type: z.literal("Expression"),
    id: z.string(),
    workId: z.string().optional(),
    manifestations: z.array(ManifestationSchema).optional(),
    aggregateManifestations: z.array(ManifestationSchema).optional(),
    title: TitleSchema.optional(),
    contributors: z.array(ContributorSchema),
    languages: z.array(LanguageSchema),
    hasParallelText: z.boolean(),
    gameTextLanguages: z.array(LanguageSchema).optional(),
    numberOfPlayers: NumberOfPlayersSchema.optional(),
    expressionType: expressionTypeSchema,
    edition: z.string().optional(),
    collections: z.array(CollectionExpressionEntrySchema),
    platforms: PlatformSchema.array().optional(),
    instrumentations: z.array(instrumentationSchema).optional(),
    intermediateTranslationLanguages: z.array(LanguageSchema).optional()
  })
).strict();
var WorkTypeSchema = z.object({
  type: z.literal("WorkType"),
  id: z.string(),
  uri: z.string(),
  label: LanguageMapWithEnglishSchema
});

// src/cordata/Genre.ts
var GenreSchema = z.object({
  type: z.literal("Genre"),
  id: z.string(),
  identifiers: z.object({
    bibbiAuthorityId: z.string().optional(),
    ntsfId: z.string().optional(),
    nmbfId: z.string().optional()
  }).strict(),
  name: LanguageMapSchema,
  alternativeNames: LanguageMapListSchema,
  domain: WorkTypeSchema,
  isForm: z.boolean(),
  scopeNote: z.string().optional(),
  _origin: originSchema.optional()
}).strict();
var isNtsfId = (id) => id.startsWith("https://id.nb.no/vocabulary/ntsf");
var SubjectSchema = z.object({
  type: z.literal("Subject"),
  id: z.string(),
  identifiers: z.object({
    bibbiAuthorityId: z.string().optional()
  }).strict(),
  focus: z.union([PlaceSchema, ConceptSchema, PersonSchema, CollectiveAgentSchema]).optional(),
  name: LanguageMapSchema,
  qualifier: LanguageMapSchema.optional(),
  specification: LanguageMapSchema.optional(),
  scopeNote: z.string().optional(),
  webdewey: z.string().optional(),
  ddk5: z.string().optional(),
  _temp_subdivisions: z.array(LanguageMapSchema),
  subdivisions: z.array(z.union([PlaceSchema, ConceptSchema])),
  temporalSubdivision: z.string().optional(),
  alternativeNames: LanguageMapListSchema
});
var UniformTitleMusicSchema = z.object({
  titleType: z.literal("uniform_title_music"),
  titleLanguage: LanguageSchema.optional(),
  form: z.string(),
  medium: z.string().optional(),
  serialNumber: z.string().optional(),
  opusNumber: z.string().optional(),
  thematicCatalogueNumber: z.string().optional(),
  part: z.string().optional(),
  key: z.string().optional(),
  arrangement: z.string().optional()
});
var ContentsEntrySchema = z.object({
  type: z.literal("ContentsEntry"),
  title: TitleSchema,
  creator: AgentSchema.optional()
});

// src/cordata/Work.ts
var WorkSchema = CordataEntityV2Schema.merge(
  z.object({
    type: z.literal("Work"),
    id: z.string(),
    identifiers: z.object({
      bibbiAuthorityId: z.string().optional(),
      deichmanWorkId: z.string().optional(),
      nbWorkId: z.string().optional()
    }).strict(),
    workYear: z.number().optional(),
    languages: z.array(LanguageSchema),
    workForm: z.string().optional(),
    title: z.union([TitleSchema, UniformTitleMusicSchema]),
    variantTitles: z.array(TitleSchema),
    contributors: z.array(ContributorSchema),
    expressions: z.array(ExpressionSchema).optional(),
    ageGroups: z.array(AgeGroupSchema),
    ageLimit: AgeLimitSchema.optional(),
    summary: z.string().optional(),
    subjects: z.array(SubjectSchema),
    genres: z.array(GenreSchema),
    workTypes: z.array(WorkTypeSchema),
    isShortWork: z.boolean().optional(),
    contents: z.array(ContentsEntrySchema).optional(),
    isMachineGenerated: z.boolean().optional(),
    isFiction: z.boolean().optional(),
    countriesOfOrigin: z.array(PlaceSchema).optional(),
    accessibleContentFeature: AccessibleContentFeatureSchema.optional()
  })
).strict();
var CordataChangeEventSchema = z.object({
  type: z.enum(["Work", "Person", "CollectiveAgent"]),
  id: z.string(),
  status: z.enum(["updated", "deleted"])
});

// src/version.ts
var version = "0.9.0";

// src/vocabularies/countries.ts
var countries = [
  {
    identifiers: {
      bibbiAuthorityId: "1438999"
    },
    id: "1438999",
    name: {
      nb: "Afghanistan",
      nn: "Afghanistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439007"
    },
    id: "1439007",
    name: {
      nb: "Albania",
      nn: "Albania"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439009"
    },
    id: "1439009",
    name: {
      nb: "Algerie",
      nn: "Algerie"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439021"
    },
    id: "1439021",
    name: {
      nb: "Andorra",
      nn: "Andorra"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439022"
    },
    id: "1439022",
    name: {
      nb: "Angola",
      nn: "Angola"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439024"
    },
    id: "1439024",
    name: {
      nb: "Antigua og Barbuda",
      nn: "Antigua og Barbuda"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439027"
    },
    id: "1439027",
    name: {
      nb: "Argentina",
      nn: "Argentina"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439032"
    },
    id: "1439032",
    name: {
      nb: "Armenia",
      nn: "Armenia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439033"
    },
    id: "1439033",
    name: {
      nb: "Aserbajdsjan",
      nn: "Aserbajdsjan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439042"
    },
    id: "1439042",
    name: {
      nb: "Australia",
      nn: "Australia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439046"
    },
    id: "1439046",
    name: {
      nb: "Bahamas",
      nn: "Bahamas"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439047"
    },
    id: "1439047",
    name: {
      nb: "Bahrain",
      nn: "Bahrain"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439052"
    },
    id: "1439052",
    name: {
      nb: "Bangladesh",
      nn: "Bangladesh"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439053"
    },
    id: "1439053",
    name: {
      nb: "Barbados",
      nn: "Barbados"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439057"
    },
    id: "1439057",
    name: {
      nb: "Belgia",
      nn: "Belgia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439058"
    },
    id: "1439058",
    name: {
      nb: "Belize",
      nn: "Belize"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439059"
    },
    id: "1439059",
    name: {
      nb: "Benin",
      nn: "Benin"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439065"
    },
    id: "1439065",
    name: {
      nb: "Bhutan",
      nn: "Bhutan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439068"
    },
    id: "1439068",
    name: {
      nb: "Mauritania",
      nn: "Mauritania"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439069"
    },
    id: "1439069",
    name: {
      nb: "Mauritius",
      nn: "Mauritius"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439075"
    },
    id: "1439075",
    name: {
      nb: "Mesopotamia",
      nn: "Mesopotamia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439077"
    },
    id: "1439077",
    name: {
      nb: "Mexico",
      nn: "Mexico"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439089"
    },
    id: "1439089",
    name: {
      nb: "Moldova",
      nn: "Moldova"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439090"
    },
    id: "1439090",
    name: {
      nb: "Monaco",
      nn: "Monaco"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439091"
    },
    id: "1439091",
    name: {
      nb: "Mongolia",
      nn: "Mongolia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439093"
    },
    id: "1439093",
    name: {
      nb: "Montenegro",
      nn: "Montenegro"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439095"
    },
    id: "1439095",
    name: {
      nb: "Mosambik",
      nn: "Mosambik"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439101"
    },
    id: "1439101",
    name: {
      nb: "F\xE6r\xF8yene",
      nn: "F\xE6r\xF8yane"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439102"
    },
    id: "1439102",
    name: {
      nb: "Gabon",
      nn: "Gabon"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439104"
    },
    id: "1439104",
    name: {
      nb: "Gambia",
      nn: "Gambia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439105"
    },
    id: "1439105",
    name: {
      nb: "Danmark",
      nn: "Danmark"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439110"
    },
    id: "1439110",
    name: {
      nb: "Dominikanske republikk",
      nn: "Dominikanske republikk"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439116"
    },
    id: "1439116",
    name: {
      nb: "Ecuador",
      nn: "Ecuador"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439120"
    },
    id: "1439120",
    name: {
      nb: "Egypt",
      nn: "Egypt"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439122"
    },
    id: "1439122",
    name: {
      nb: "Ekvatorial-Guinea",
      nn: "Ekvatorial-Guinea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439123"
    },
    id: "1439123",
    name: {
      nb: "El Salvador",
      nn: "El Salvador"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439124"
    },
    id: "1439124",
    name: {
      nb: "Elfenbenskysten",
      nn: "Elfenbenskysten"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439125"
    },
    id: "1439125",
    name: {
      nb: "England (Storbritannia)",
      nn: "England (Storbritannia)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439126"
    },
    id: "1439126",
    name: {
      nb: "Eritrea",
      nn: "Eritrea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439127"
    },
    id: "1439127",
    name: {
      nb: "Estland",
      nn: "Estland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439128"
    },
    id: "1439128",
    name: {
      nb: "Etiopia",
      nn: "Etiopia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439135"
    },
    id: "1439135",
    name: {
      nb: "Filippinene",
      nn: "Filippinane"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439137"
    },
    id: "1439137",
    name: {
      nb: "Finland",
      nn: "Finland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439144"
    },
    id: "1439144",
    name: {
      nb: "USA",
      nn: "USA"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439146"
    },
    id: "1439146",
    name: {
      nb: "Frankrike",
      nn: "Frankrike"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439153"
    },
    id: "1439153",
    name: {
      nb: "Georgia",
      nn: "Georgia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439154"
    },
    id: "1439154",
    name: {
      nb: "Ghana",
      nn: "Ghana"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439162"
    },
    id: "1439162",
    name: {
      nb: "Gr\xF8nland",
      nn: "Gr\xF8nland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439163"
    },
    id: "1439163",
    name: {
      nb: "Guatemala",
      nn: "Guatemala"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439165"
    },
    id: "1439165",
    name: {
      nb: "Guinea-Bissau",
      nn: "Guinea-Bissau"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439166"
    },
    id: "1439166",
    name: {
      nb: "Guinea",
      nn: "Guinea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439167"
    },
    id: "1439167",
    name: {
      nb: "Guyana",
      nn: "Guyana"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439170"
    },
    id: "1439170",
    name: {
      nb: "Haiti",
      nn: "Haiti"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439182"
    },
    id: "1439182",
    name: {
      nb: "Hellas",
      nn: "Hellas"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439200"
    },
    id: "1439200",
    name: {
      nb: "India",
      nn: "India"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439203"
    },
    id: "1439203",
    name: {
      nb: "Indonesia",
      nn: "Indonesia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439206"
    },
    id: "1439206",
    name: {
      nb: "Irak",
      nn: "Irak"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439207"
    },
    id: "1439207",
    name: {
      nb: "Iran",
      nn: "Iran"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439209"
    },
    id: "1439209",
    name: {
      nb: "Island",
      nn: "Island"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439210"
    },
    id: "1439210",
    name: {
      nb: "Israel",
      nn: "Israel"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439213"
    },
    id: "1439213",
    name: {
      nb: "Italia",
      nn: "Italia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439214"
    },
    id: "1439214",
    name: {
      nb: "Jamaica",
      nn: "Jamaica"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439216"
    },
    id: "1439216",
    name: {
      nb: "Japan",
      nn: "Japan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439221"
    },
    id: "1439221",
    name: {
      nb: "Jordan",
      nn: "Jordan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439224"
    },
    id: "1439224",
    name: {
      nb: "Jugoslavia",
      nn: "Jugoslavia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439228"
    },
    id: "1439228",
    name: {
      nb: "Bulgaria",
      nn: "Bulgaria"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439229"
    },
    id: "1439229",
    name: {
      nb: "Burkina Faso",
      nn: "Burkina Faso"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439230"
    },
    id: "1439230",
    name: {
      nb: "Burundi",
      nn: "Burundi"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439239"
    },
    id: "1439239",
    name: {
      nb: "Canada",
      nn: "Canada"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439244"
    },
    id: "1439244",
    name: {
      nb: "Chile",
      nn: "Chile"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439245"
    },
    id: "1439245",
    name: {
      nb: "Colombia",
      nn: "Colombia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439252"
    },
    id: "1439252",
    name: {
      nb: "Costa Rica",
      nn: "Costa Rica"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439254"
    },
    id: "1439254",
    name: {
      nb: "Cuba",
      nn: "Cuba"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439259"
    },
    id: "1439259",
    name: {
      nb: "Kamerun",
      nn: "Kamerun"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439260"
    },
    id: "1439260",
    name: {
      nb: "Kambodsja",
      nn: "Kambodsja"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439265"
    },
    id: "1439265",
    name: {
      nb: "Kapp Verde",
      nn: "Kapp Verde"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439271"
    },
    id: "1439271",
    name: {
      nb: "Kasakhstan",
      nn: "Kasakhstan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439276"
    },
    id: "1439276",
    name: {
      nb: "Kenya",
      nn: "Kenya"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439278"
    },
    id: "1439278",
    name: {
      nb: "Kina",
      nn: "Kina"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439279"
    },
    id: "1439279",
    name: {
      nb: "Kirgisistan",
      nn: "Kirgisistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439282"
    },
    id: "1439282",
    name: {
      nb: "Komorene (staten)",
      nn: "Komorane (staten)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439284"
    },
    id: "1439284",
    name: {
      nb: "Kongo-Brazzaville",
      nn: "Kongo-Brazzaville"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439286"
    },
    id: "1439286",
    name: {
      nb: "Uruguay",
      nn: "Uruguay"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439287"
    },
    id: "1439287",
    name: {
      nb: "Usbekistan",
      nn: "Usbekistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439296"
    },
    id: "1439296",
    name: {
      nb: "Venezuela",
      nn: "Venezuela"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439303"
    },
    id: "1439303",
    name: {
      nb: "Samoa",
      nn: "Samoa"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439308"
    },
    id: "1439308",
    name: {
      nb: "Vietnam",
      nn: "Vietnam"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439314"
    },
    id: "1439314",
    name: {
      nb: "Wales (Storbritannia)",
      nn: "Wales (Storbritannia)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439321"
    },
    id: "1439321",
    name: {
      nb: "Jemen",
      nn: "Jemen"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439322"
    },
    id: "1439322",
    name: {
      nb: "Zambia",
      nn: "Zambia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439324"
    },
    id: "1439324",
    name: {
      nb: "Zimbabwe",
      nn: "Zimbabwe"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439328"
    },
    id: "1439328",
    name: {
      nb: "\xD8sterrike",
      nn: "Austerrike"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439332"
    },
    id: "1439332",
    name: {
      nb: "\xC5land (Finland)",
      nn: "\xC5land (Finland)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439336"
    },
    id: "1439336",
    name: {
      nb: "Skottland (Storbritannia)",
      nn: "Skottland (Storbritannia)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439337"
    },
    id: "1439337",
    name: {
      nb: "Tanzania",
      nn: "Tanzania"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439340"
    },
    id: "1439340",
    name: {
      nb: "Norge",
      nn: "Noreg"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439341"
    },
    id: "1439341",
    name: {
      nb: "Korea",
      nn: "Korea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439348"
    },
    id: "1439348",
    name: {
      nb: "Malta",
      nn: "Malta"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439354"
    },
    id: "1439354",
    name: {
      nb: "Sierra Leone",
      nn: "Sierra Leone"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439358"
    },
    id: "1439358",
    name: {
      nb: "Somalia",
      nn: "Somalia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439359"
    },
    id: "1439359",
    name: {
      nb: "Puerto Rico",
      nn: "Puerto Rico"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    //MAKEDONIA
    identifiers: {
      bibbiAuthorityId: "1439361"
    },
    id: "1439361",
    name: {
      nb: "Nord-Makedonia",
      nn: "Nord-Makedonia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439362"
    },
    id: "1439362",
    name: {
      nb: "Russland",
      nn: "Russland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439364"
    },
    id: "1439364",
    name: {
      nb: "Ukraina",
      nn: "Ukraina"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439367"
    },
    id: "1439367",
    name: {
      nb: "Slovakia",
      nn: "Slovakia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439368"
    },
    id: "1439368",
    name: {
      nb: "Marokko",
      nn: "Marokko"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439369"
    },
    id: "1439369",
    name: {
      nb: "Namibia",
      nn: "Namibia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439371"
    },
    id: "1439371",
    name: {
      nb: "Laos",
      nn: "Laos"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439373"
    },
    id: "1439373",
    name: {
      nb: "Tibet (Kina)",
      nn: "Tibet (Kina)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439374"
    },
    id: "1439374",
    name: {
      nb: "Paraguay",
      nn: "Paraguay"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439375"
    },
    id: "1439375",
    name: {
      nb: "Syria",
      nn: "Syria"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439378"
    },
    id: "1439378",
    name: {
      nb: "Nicaragua",
      nn: "Nicaragua"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439379"
    },
    id: "1439379",
    name: {
      nb: "Libya",
      nn: "Libya"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439380"
    },
    id: "1439380",
    name: {
      nb: "Nederland",
      nn: "Nederland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439387"
    },
    id: "1439387",
    name: {
      nb: "Thailand",
      nn: "Thailand"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439388"
    },
    id: "1439388",
    name: {
      nb: "Brunei",
      nn: "Brunei"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439389"
    },
    id: "1439389",
    name: {
      nb: "Malaysia",
      nn: "Malaysia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439392"
    },
    id: "1439392",
    name: {
      nb: "Singapore",
      nn: "Singapore"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439399"
    },
    id: "1439399",
    name: {
      nb: "Surinam",
      nn: "Surinam"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439401"
    },
    id: "1439401",
    name: {
      nb: "Sverige",
      nn: "Sverige"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439402"
    },
    id: "1439402",
    name: {
      nb: "Libanon",
      nn: "Libanon"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439407"
    },
    id: "1439407",
    name: {
      nb: "Romania",
      nn: "Romania"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439410"
    },
    id: "1439410",
    name: {
      nb: "Liechtenstein",
      nn: "Liechtenstein"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439425"
    },
    id: "1439425",
    name: {
      nb: "Tyskland",
      nn: "Tyskland"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439426"
    },
    id: "1439426",
    name: {
      nb: "Kroatia",
      nn: "Kroatia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439427"
    },
    id: "1439427",
    name: {
      nb: "Serbia",
      nn: "Serbia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439428"
    },
    id: "1439428",
    name: {
      nb: "Mali",
      nn: "Mali"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439429"
    },
    id: "1439429",
    name: {
      nb: "Niger (staten)",
      nn: "Niger (staten)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439430"
    },
    id: "1439430",
    name: {
      nb: "S\xF8r-Afrika",
      nn: "S\xF8r-Afrika"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439431"
    },
    id: "1439431",
    name: {
      nb: "Luxembourg",
      nn: "Luxembourg"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439436"
    },
    id: "1439436",
    name: {
      nb: "Papua Ny-Guinea",
      nn: "Papua Ny-Guinea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439439"
    },
    id: "1439439",
    name: {
      nb: "Myanmar",
      nn: "Myanmar"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439442"
    },
    id: "1439442",
    name: {
      nb: "Nord-Irland (Storbritannia)",
      nn: "Nord-Irland (Storbritannia)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439443"
    },
    id: "1439443",
    name: {
      nb: "Liberia",
      nn: "Liberia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439448"
    },
    id: "1439448",
    name: {
      nb: "Togo",
      nn: "Togo"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439458"
    },
    id: "1439458",
    name: {
      nb: "Kuwait",
      nn: "Kuwait"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439460"
    },
    id: "1439460",
    name: {
      nb: "Qatar",
      nn: "Qatar"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439461"
    },
    id: "1439461",
    name: {
      nb: "Saudi-Arabia",
      nn: "Saudi-Arabia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439463"
    },
    id: "1439463",
    name: {
      nb: "Peru",
      nn: "Peru"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439465"
    },
    id: "1439465",
    name: {
      nb: "Ungarn",
      nn: "Ungarn"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439467"
    },
    id: "1439467",
    name: {
      nb: "Latvia",
      nn: "Latvia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439468"
    },
    id: "1439468",
    name: {
      nb: "Litauen",
      nn: "Litauen"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439469"
    },
    id: "1439469",
    name: {
      nb: "Polen",
      nn: "Polen"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439470"
    },
    id: "1439470",
    name: {
      nb: "Den sentralafrikanske republikk",
      nn: "Den sentralafrikanske republikk"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439473"
    },
    id: "1439473",
    name: {
      nb: "Tsjad",
      nn: "Tsjad"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439478"
    },
    id: "1439478",
    name: {
      nb: "Nepal",
      nn: "Nepal"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439479"
    },
    id: "1439479",
    name: {
      nb: "Pakistan",
      nn: "Pakistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439480"
    },
    id: "1439480",
    name: {
      nb: "Sri Lanka",
      nn: "Sri Lanka"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439482"
    },
    id: "1439482",
    name: {
      nb: "Uganda",
      nn: "Uganda"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439484"
    },
    id: "1439484",
    name: {
      nb: "Malawi",
      nn: "Malawi"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439494"
    },
    id: "1439494",
    name: {
      nb: "Salomon\xF8yene",
      nn: "Salomon\xF8yane"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439496"
    },
    id: "1439496",
    name: {
      nb: "Storbritannia",
      nn: "Storbritannia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439498"
    },
    id: "1439498",
    name: {
      nb: "Tadsjikistan",
      nn: "Tadsjikistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439500"
    },
    id: "1439500",
    name: {
      nb: "Turkmenistan",
      nn: "Turkmenistan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439504"
    },
    id: "1439504",
    name: {
      nb: "Brasil",
      nn: "Brasil"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439511"
    },
    id: "1439511",
    name: {
      nb: "Taiwan (Kina)",
      nn: "Taiwan (Kina)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439512"
    },
    id: "1439512",
    name: {
      nb: "Tyrkia",
      nn: "Tyrkia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439514"
    },
    id: "1439514",
    name: {
      nb: "Saint Lucia",
      nn: "Saint Lucia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439516"
    },
    id: "1439516",
    name: {
      nb: "Trinidad og Tobago",
      nn: "Trinidad og Tobago"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439519"
    },
    id: "1439519",
    name: {
      nb: "Nigeria",
      nn: "Nigeria"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439522"
    },
    id: "1439522",
    name: {
      nb: "Madagaskar",
      nn: "Madagaskar"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439524"
    },
    id: "1439524",
    name: {
      nb: "Seychellene",
      nn: "Seychellane"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439526"
    },
    id: "1439526",
    name: {
      nb: "Panama",
      nn: "Panama"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439527"
    },
    id: "1439527",
    name: {
      nb: "Nord-Korea",
      nn: "Nord-Korea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439528"
    },
    id: "1439528",
    name: {
      nb: "New Zealand",
      nn: "New Zealand"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439532"
    },
    id: "1439532",
    name: {
      nb: "Rwanda",
      nn: "Rwanda"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439534"
    },
    id: "1439534",
    name: {
      nb: "Portugal",
      nn: "Portugal"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439537"
    },
    id: "1439537",
    name: {
      nb: "Spania",
      nn: "Spania"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439538"
    },
    id: "1439538",
    name: {
      nb: "Sveits",
      nn: "Sveits"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439545"
    },
    id: "1439545",
    name: {
      nb: "Sudan",
      nn: "Sudan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439546"
    },
    id: "1439546",
    name: {
      nb: "Tunisia",
      nn: "Tunisia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439547"
    },
    id: "1439547",
    name: {
      nb: "Botswana",
      nn: "Botswana"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439548"
    },
    id: "1439548",
    name: {
      nb: "Lesotho",
      nn: "Lesotho"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439551"
    },
    id: "1439551",
    name: {
      nb: "Eswatini",
      nn: "Eswatini"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439557"
    },
    id: "1439557",
    name: {
      nb: "Senegal",
      nn: "Senegal"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439560"
    },
    id: "1439560",
    name: {
      nb: "Palestina",
      nn: "Palestina"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439562"
    },
    id: "1439562",
    name: {
      nb: "Kypros",
      nn: "Kypros"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439563"
    },
    id: "1439563",
    name: {
      nb: "S\xF8r-Korea",
      nn: "S\xF8r-Korea"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439654"
    },
    id: "1439654",
    name: {
      nb: "Bosnia-Hercegovina",
      nn: "Bosnia-Hercegovina"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439655"
    },
    id: "1439655",
    name: {
      nb: "Tsjekkia",
      nn: "Tsjekkia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439674"
    },
    id: "1439674",
    name: {
      nb: "Kosovo",
      nn: "Kosovo"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439764"
    },
    id: "1439764",
    name: {
      nb: "Slovenia",
      nn: "Slovenia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439880"
    },
    id: "1439880",
    name: {
      nb: "Kongo (demokratisk republikk)",
      nn: "Kongo (demokratisk republikk)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439986"
    },
    id: "1439986",
    name: {
      nb: "Honduras",
      nn: "Honduras"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440081"
    },
    id: "1440081",
    name: {
      nb: "Irland (republikken)",
      nn: "Irland (republikken)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440094"
    },
    id: "1440094",
    name: {
      nb: "Forente arabiske emirater",
      nn: "Forente arabiske emirater"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440305"
    },
    id: "1440305",
    name: {
      nb: "S\xF8r-Sudan",
      nn: "S\xF8r-Sudan"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440574"
    },
    id: "1440574",
    name: {
      nb: "Guadeloupe",
      nn: "Guadeloupe"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440580"
    },
    id: "1440580",
    name: {
      nb: "Grenada",
      nn: "Grenada"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1440906"
    },
    id: "1440906",
    name: {
      nb: "Isle of Man (Storbritannia)",
      nn: "Isle of Man (Storbritannia)"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1441079"
    },
    id: "1441079",
    name: {
      nb: "Belarus",
      nn: "Belarus"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1441163"
    },
    id: "1441163",
    name: {
      nb: "Persia",
      nn: "Persia"
    },
    type: "Place",
    placeType: "Country",
    alternativeNames: {
      nb: [],
      nn: []
    }
  },
  {
    identifiers: {
      bibbiAuthorityId: "1439188"
    },
    id: "1439188",
    name: {
      nb: "Hongkong (Kina)",
      nn: "Hongkong (Kina)"
    },
    alternativeNames: {
      nb: [],
      nn: []
    },
    type: "Place"
  }
];

// src/vocabularies/documentTypeList.ts
var documentTypeList = [
  {
    type: "DocumentType",
    id: "1",
    code: "ab",
    format: "Atlas",
    medium: void 0,
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "2",
    code: "ab,gd",
    format: "Atlas",
    medium: "CD-ROM",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "3",
    code: "aj",
    format: "Kart",
    medium: void 0,
    defaultWorkTypeId: void 0,
    onixCodes: ["CB", "CD"]
  },
  {
    type: "DocumentType",
    id: "5",
    code: "l",
    format: "Bok",
    medium: void 0,
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: ["BB", "BC", "BE", "BH", "BL"]
  },
  {
    type: "DocumentType",
    id: "6",
    code: "dc,la",
    format: "E-bok",
    medium: "CD",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "7",
    code: "gd,la",
    format: "E-bok",
    medium: "CD-ROM",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "9",
    code: "ga,la,nb,o",
    format: "E-bok",
    medium: "nedlastbar Epub med DRM",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "10",
    code: "dd,di",
    format: "Lydbok",
    medium: "avspiller med lydfil",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: ["AK"]
  },
  {
    type: "DocumentType",
    id: "11",
    code: "dc,di,gt",
    format: "Lydbok",
    medium: "CD Daisy",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "12",
    code: "dc,di,dz",
    format: "Lydbok",
    medium: "CD MP3",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: ["AC:A103"]
  },
  {
    type: "DocumentType",
    id: "13",
    code: "dc,di",
    format: "Lydbok",
    medium: "CD",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: ["AC"]
  },
  {
    type: "DocumentType",
    id: "14",
    code: "de,di",
    format: "Lydbok",
    medium: "digikort",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "15",
    code: "di,ee",
    format: "Lydbok",
    medium: "DVD",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "16",
    code: "di,dz,gc",
    format: "Lydbok",
    medium: "DVD-ROM MP3",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "17",
    code: "db,di",
    format: "Lydbok",
    medium: "kassett",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: ["AB"]
  },
  {
    type: "DocumentType",
    id: "18",
    code: "di,ga,nl,o",
    format: "E-lydbok",
    medium: "nedlastbar WMA med DRM",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000"],
    onixCodes: ["AJ:A103"]
  },
  {
    type: "DocumentType",
    id: "19",
    code: "ee",
    format: "Film",
    medium: "DVD",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "20",
    code: "ef",
    format: "Film",
    medium: "Blu-ray",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "21",
    code: "ef,eg",
    format: "Film",
    medium: "Blu-ray 3D",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "22",
    code: "ee,ef",
    format: "Film",
    medium: "DVD & Blu-ray",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "23",
    code: "ee,ef,eg",
    format: "Film",
    medium: "DVD & Blu-ray 3D",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "24",
    code: "ed",
    format: "Film",
    medium: "VHS",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "25",
    code: "dd,dg",
    format: "Musikk",
    medium: "avspiller med lydfil",
    defaultWorkTypeId: "V1001",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "26",
    code: "dc,dg",
    format: "Musikk",
    medium: "CD",
    defaultWorkTypeId: "V1001",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "27",
    code: "c",
    format: "Musikktrykk",
    medium: void 0,
    defaultWorkTypeId: "V1001",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "28",
    code: "gc,mj",
    format: "Dataspill",
    medium: "Xbox 360",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "29",
    code: "gf,mn",
    format: "Dataspill",
    medium: "Nintendo DS",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "30",
    code: "gi,mo",
    format: "Dataspill",
    medium: "Nintendo Wii",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "31",
    code: "gd,ma",
    format: "Dataspill",
    medium: "PC, CD-ROM",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "32",
    code: "gc,ma",
    format: "Dataspill",
    medium: "PC, DVD-ROM",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "33",
    code: "gc,mb",
    format: "Dataspill",
    medium: "Playstation",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "34",
    code: "gg,mc",
    format: "Dataspill",
    medium: "Playstation 3",
    defaultWorkTypeId: "V1005",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "35",
    code: "eg,gg,mc",
    format: "Dataspill",
    medium: "Playstation 3 3D",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "36",
    code: "gh,md",
    format: "Dataspill",
    medium: "Playstation Portable",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "37",
    code: "gg,me",
    format: "Dataspill",
    medium: "Playstation 4",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "38",
    code: "gg,mi",
    format: "Dataspill",
    medium: "Xbox",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "40",
    code: "gg,mk",
    format: "Dataspill",
    medium: "Xbox One",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "41",
    code: "dc,dh",
    format: "Spr\xE5kkurs",
    medium: "CD",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "42",
    code: "dh,gd",
    format: "Spr\xE5kkurs",
    medium: "CD-ROM",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "43",
    code: "dh,gc",
    format: "Spr\xE5kkurs",
    medium: "DVD-ROM",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "44",
    code: "dh,dk",
    format: "Spr\xE5kkurs",
    medium: "kombidokument",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "45",
    code: "dk",
    format: "Kombidokument",
    medium: void 0,
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "46",
    code: "ge",
    format: "Nettressurs",
    medium: void 0,
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "47",
    code: "h",
    format: "Tredimensjonal gjenstand",
    medium: void 0,
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "48",
    code: "da,dg",
    format: "Musikk",
    medium: "LP",
    defaultWorkTypeId: "V1001",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "51",
    code: "db,dg",
    format: "Musikk",
    medium: "kassett",
    defaultWorkTypeId: "V1001",
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "52",
    code: "db,dh",
    format: "Spr\xE5kkurs",
    medium: "kassett",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "55",
    code: "gd",
    format: "CD-ROM",
    medium: void 0,
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "56",
    code: "eh",
    format: "Film",
    medium: "Ultra HD Blu-ray",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "58",
    code: "gf,mr",
    format: "Dataspill",
    medium: "Nintendo 3DS",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "59",
    code: "dc,dh,dz",
    format: "Spr\xE5kkurs",
    medium: "CD MP3",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "60",
    code: "dh,ga",
    format: "Spr\xE5kkurs",
    medium: "nedlastbar fil",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "61",
    code: "j",
    format: "Periodikum",
    medium: "trykt",
    defaultWorkTypeId: void 0,
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "62",
    code: "ef,eh",
    format: "Film",
    medium: "Blu-ray & Ultra HD Blu-ray",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "70",
    code: "gi,mp",
    format: "Dataspill",
    medium: "Nintendo Wii U",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "71",
    code: "gf,ms",
    format: "Dataspill",
    medium: "Nintendo Switch",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "73",
    code: "l,f",
    format: "Bok",
    medium: "illustrert",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "74",
    code: "ga,la,nb,o,f",
    format: "E-bok",
    medium: "nedlastbar Epub med DRM (illustrert)",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "75",
    code: "le,f",
    format: "Ark",
    medium: "illustrert",
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "76",
    code: "le",
    format: "Ark",
    medium: void 0,
    defaultWorkTypeId: "V1000",
    allowedWorkTypeIds: ["V1000", "V1003"],
    onixCodes: []
  },
  // {
  //   type: "DocumentType",
  //   id: "77",
  //   code: undefined,
  //   format: "************************************",
  //   medium: undefined,
  //   defaultWorkTypeId: undefined,
  // },
  {
    type: "DocumentType",
    id: "78",
    code: "gj,mf",
    format: "Dataspill",
    medium: "Playstation 5",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "79",
    code: "gj,ml",
    format: "Dataspill",
    medium: "Xbox Series X",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "80",
    code: "gj,mk,ml",
    format: "Dataspill",
    medium: "Xbox One & Xbox Series X",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "81",
    code: "gj,me,mf",
    format: "Dataspill",
    medium: "Playstation 4 & Playstation 5",
    defaultWorkTypeId: "V1005",
    allowedWorkTypeIds: ["V1005"],
    onixCodes: []
  },
  {
    type: "DocumentType",
    id: "83",
    code: "ea",
    format: "Film",
    medium: "str\xF8mningsbar video",
    defaultWorkTypeId: "V1002",
    allowedWorkTypeIds: ["V1002"],
    onixCodes: []
  }
  // {
  //   type: "DocumentType",
  //   id: "82",
  //   code: "m",
  //   format: "Dataspill",
  //   medium: "K-fond",
  //   defaultWorkTypeId: "V1005",
  //   allowedWorkTypeIds: ["V1005"],
  // },
];
var isDocumentTypeCode = (documentTypeCode) => documentTypeList.some(({ code }) => code === documentTypeCode);
var getDocumentTypeFromCode = (code) => {
  const documentType = documentTypeList.find((documentType2) => documentType2.code === code);
  if (!documentType) throw new Error(`Document type '${code}' not found`);
  return documentType;
};
var documentTypeFormatCodes = {
  "Atlas": "atlas",
  "Kart": "map",
  "Bok": "book",
  "E-bok": "ebook",
  "Lydbok": "audiobook",
  "E-lydbok": "digital-audiobook",
  "Film": "movie",
  "Musikk": "music",
  "Musikktrykk": "sheet-music",
  "Dataspill": "videogame",
  "Spr\xE5kkurs": "language-course",
  "Kombidokument": "mixed",
  "Nettressurs": "website",
  "Tredimensjonal gjenstand": "artifact",
  "CD-ROM": "other",
  "Periodikum": "periodical",
  "Ark": "other"
  // TODO
};
var getDocumentTypeFormatCode = (code) => {
  const documentType = getDocumentTypeFromCode(code);
  return documentTypeFormatCodes[documentType.format];
};
var documentTypeFormatTranslations = {
  "Atlas": "Atlas",
  "Kart": "Kart",
  "Bok": "Bok",
  "E-bok": "E-bok",
  "Lydbok": "Lydbok",
  "E-lydbok": "E-lydbok",
  "Film": "Film",
  "Musikk": "Musikk",
  "Musikktrykk": "Musikktrykk",
  "Dataspill": "Dataspel",
  "Spr\xE5kkurs": "Spr\xE5kkurs",
  "Kombidokument": "Kombidokument",
  "Nettressurs": "Nettressurs",
  "Tredimensjonal gjenstand": "Tredimensjonal gjenstand",
  "CD-ROM": "CD-ROM",
  "Periodikum": "Periodikum",
  "Ark": "Ark"
};
var translateDocumentTypeFormat = (format) => documentTypeFormatTranslations[format];

// src/vocabularies/movieGenreList.ts
var movieGenreList = [
  {
    id: "1165829",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1128", bibbiAuthorityId: "1165829" },
    name: { nb: "Action", nn: "Action" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1165839",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1130", bibbiAuthorityId: "1165839" },
    name: { nb: "Adaptasjoner (film)", nn: "Adaptasjoner (film)" },
    alternativeNames: {
      nb: ["Filmatisert bok", "Filmadaptasjoner", "Filmatiserte b\xF8ker"],
      nn: ["Filmatisert bok", "Filmadaptasjonar", "Filmatiserte b\xF8ker"]
    }
  },
  {
    id: "1165840",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1135", bibbiAuthorityId: "1165840" },
    name: { nb: "Animasjonsfilmer", nn: "Animasjonsfilmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203109",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1140", bibbiAuthorityId: "1203109" },
    name: { nb: "Biografiske filmer", nn: "Biografiske filmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1165870",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1202", bibbiAuthorityId: "1165870" },
    name: {
      nb: "Dokumentarfilmer",
      nn: "Dokumentarfilmar"
    },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1165830",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1319", bibbiAuthorityId: "1165830" },
    name: { nb: "Drama (film)", nn: "Drama (film)" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203088",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1301", bibbiAuthorityId: "1203088" },
    name: {
      nb: "Eventyrfilmer",
      nn: "Eventyrfilmar"
    },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203114",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1149", bibbiAuthorityId: "1203114" },
    name: { nb: "Fantasy (film)", nn: "Fantasy (film)" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203111",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1213", bibbiAuthorityId: "1203111" },
    name: { nb: "Historiske filmer", nn: "Historiske filmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1165828",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1150", bibbiAuthorityId: "1165828" },
    name: { nb: "Komedier (film)", nn: "Komedier (film)" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1165697",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1156", bibbiAuthorityId: "1165697" },
    name: { nb: "Krigsfilmer", nn: "Krigsfilmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203112",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1196", bibbiAuthorityId: "1203112" },
    name: { nb: "Krim (film)", nn: "Krim (film)" },
    alternativeNames: { nb: ["Kriminalfilmer"], nn: ["Kriminalfilmar"] }
  },
  {
    id: "1165998",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1290", bibbiAuthorityId: "1165998" },
    name: { nb: "Musikkfilmer", nn: "Musikkfilmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1166061",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1236", bibbiAuthorityId: "1166061" },
    name: { nb: "Naturfilmer", nn: "Naturfilmar" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1204057",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1169", bibbiAuthorityId: "1204057" },
    name: { nb: "Romantiske filmer", nn: "Romantiske filmar" },
    alternativeNames: { nb: ["Romantikk (film)"], nn: ["Romantikk (film)"] }
  },
  {
    id: "1203083",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1188", bibbiAuthorityId: "1203083" },
    name: {
      nb: "Science fiction (film)",
      nn: "Science fiction (film)"
    },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203108",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1333", bibbiAuthorityId: "1203108" },
    name: { nb: "Gr\xF8ssere (film)", nn: "Gr\xF8ssarar (film)" },
    alternativeNames: { nb: ["Horror (film)", "Skrekkfilmer"], nn: ["Horror (film)", "Skrekkfilmar"] }
  },
  {
    id: "1203086",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1181", bibbiAuthorityId: "1203086" },
    name: { nb: "Thrillere (film)", nn: "Thrillarar (film)" },
    alternativeNames: { nb: [], nn: [] }
  },
  {
    id: "1203087",
    type: "Genre",
    identifiers: { ntsfId: "https://id.nb.no/vocabulary/ntsf/1184", bibbiAuthorityId: "1203087" },
    name: { nb: "Western (film)", nn: "Western (film)" },
    alternativeNames: {
      nb: ["Westernfilmer"],
      nn: ["Westernfilmar"]
    }
  }
];

// src/vocabularies/nmaAgeLimitList.ts
var nmaAgeLimitList = [
  {
    id: "9",
    type: "NmaAgeLimit",
    years: 12,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1003"
  },
  {
    id: "4",
    type: "NmaAgeLimit",
    years: 15,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1004"
  },
  {
    id: "5",
    type: "NmaAgeLimit",
    years: 18,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1005"
  },
  {
    id: "11",
    type: "NmaAgeLimit",
    years: 6,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1001"
  },
  {
    id: "12",
    type: "NmaAgeLimit",
    years: 9,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1002"
  },
  {
    id: "10",
    type: "NmaAgeLimit",
    years: 0,
    uri: "https://schema.nb.no/Bibliographic/Values/NM1000"
  }
];

// src/vocabularies/platforms.ts
var platformList = [
  // PC
  {
    type: "Platform",
    id: "pc",
    code: "ma",
    name: "PC"
  },
  // Xbox
  {
    type: "Platform",
    id: "xbox",
    code: "mi",
    name: "Xbox"
  },
  {
    type: "Platform",
    id: "xbox-360",
    code: "mj",
    name: "Xbox 360"
  },
  {
    type: "Platform",
    id: "xbox-one",
    code: "mk",
    name: "Xbox One"
  },
  {
    type: "Platform",
    id: "xbox-sx",
    code: "ml",
    name: "Xbox Series X",
    abbreviatedName: "Xbox SX"
  },
  // Nintendo
  {
    type: "Platform",
    id: "ds",
    code: "mn",
    name: "Nintendo DS",
    abbreviatedName: "DS"
  },
  {
    type: "Platform",
    id: "wiiu",
    code: "mp",
    name: "Nintendo Wii U",
    abbreviatedName: "Wii U"
  },
  {
    type: "Platform",
    id: "wii",
    code: "mo",
    name: "Nintendo Wii",
    abbreviatedName: "Wii"
  },
  {
    type: "Platform",
    id: "3ds",
    code: "mr",
    name: "Nintendo 3DS",
    abbreviatedName: "3DS"
  },
  {
    type: "Platform",
    id: "switch",
    code: "ms",
    name: "Nintendo Switch",
    abbreviatedName: "Switch"
  },
  // PlayStation
  {
    type: "Platform",
    id: "ps",
    code: "mb",
    name: "Playstation",
    abbreviatedName: "PS"
  },
  {
    type: "Platform",
    id: "ps3",
    code: "mc",
    name: "Playstation 3",
    abbreviatedName: "PS3"
  },
  {
    type: "Platform",
    id: "psp",
    code: "md",
    name: "Playstation Portable",
    abbreviatedName: "PSP"
  },
  {
    type: "Platform",
    id: "ps4",
    code: "me",
    name: "Playstation 4",
    abbreviatedName: "PS4"
  },
  {
    type: "Platform",
    id: "ps5",
    code: "mf",
    name: "Playstation 5",
    abbreviatedName: "PS5"
  }
];
var allPlatformCodes = platformList.map((platform) => platform.code);
var isPlatformCode = (code) => allPlatformCodes.includes(code);

// src/vocabularies/roles.ts
var roles = [
  {
    type: "Role",
    id: "1",
    code: "bearb.",
    label: { nb: "bearbeider", nn: "omarbeidar" },
    marc21Code: "aut",
    normarcCode: "bearb.",
    onixCodes: ["B03", "B04"]
  },
  {
    type: "Role",
    id: "2",
    code: "dir.",
    label: { nb: "dirigent", nn: "dirigent" },
    marc21Code: "cnd",
    normarcCode: "dir.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "3",
    code: "forf.",
    label: { nb: "forfatter", nn: "forfattar" },
    marc21Code: "aut",
    normarcCode: "forf.",
    onixCodes: ["A01", "A02", "A14", "A28", "A31", "A42", "A45"]
  },
  {
    type: "Role",
    id: "4",
    code: "fotogr.",
    label: { nb: "fotograf", nn: "fotograf" },
    marc21Code: "pht",
    normarcCode: "fotogr.",
    onixCodes: ["A08", "A13"]
  },
  {
    type: "Role",
    id: "5",
    code: "illustr.",
    label: { nb: "illustrat\xF8r", nn: "illustrat\xF8r" },
    marc21Code: "ill",
    normarcCode: "illustr.",
    onixCodes: ["A12"]
  },
  {
    type: "Role",
    id: "6",
    code: "innl.",
    label: { nb: "innleser", nn: "innlesar" },
    marc21Code: "nrt",
    normarcCode: "innl.",
    onixCodes: ["E07"]
  },
  {
    type: "Role",
    id: "7",
    code: "komm.",
    label: { nb: "kommentator", nn: "kommentator" },
    marc21Code: "cmm",
    normarcCode: "komm.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "8",
    code: "komp.",
    label: { nb: "komponist", nn: "komponist" },
    marc21Code: "cmp",
    normarcCode: "komp.",
    onixCodes: ["A06"]
  },
  {
    type: "Role",
    id: "9",
    code: "manusforf.",
    label: { nb: "manusforfatter", nn: "manusforfattar" },
    marc21Code: "aus",
    normarcCode: "manusforf.",
    onixCodes: ["A03"]
  },
  {
    type: "Role",
    id: "10",
    code: "medarb.",
    label: { nb: "medarbeider/bidragsyter", nn: "medarbeidar/bidragsytar" },
    marc21Code: "ctb",
    normarcCode: "medarb.",
    onixCodes: ["A32"]
  },
  {
    type: "Role",
    id: "11",
    code: "medforf.",
    label: { nb: "medforfatter (ikke i bruk)", nn: "medforfatter (ikkje i bruk)" },
    marc21Code: "aut",
    normarcCode: "medforf.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "12",
    code: "oppr.forf.",
    label: { nb: "opprinnelig forfatter", nn: "opphavleg forfattar" },
    marc21Code: "ant",
    normarcCode: "oppr.forf.",
    onixCodes: ["A37", "A38"]
  },
  {
    type: "Role",
    id: "13",
    code: "overs.",
    label: { nb: "oversetter", nn: "omsetjar" },
    marc21Code: "trl",
    normarcCode: "overs.",
    onixCodes: ["B06", "B08", "B10"]
  },
  {
    type: "Role",
    id: "14",
    code: "red.",
    label: { nb: "redakt\xF8r", nn: "redakt\xF8r" },
    marc21Code: "edt",
    normarcCode: "red.",
    onixCodes: ["B01", "B12", "B13"]
  },
  {
    type: "Role",
    id: "15",
    code: "regiss\xF8r",
    label: { nb: "regiss\xF8r", nn: "regiss\xF8r" },
    marc21Code: "drt",
    normarcCode: "regiss\xF8r",
    onixCodes: ["D02"]
  },
  {
    type: "Role",
    id: "16",
    code: "skuesp.",
    label: { nb: "skuespiller", nn: "skodespelar" },
    marc21Code: "act",
    normarcCode: "skuesp.",
    onixCodes: ["E01"]
  },
  {
    type: "Role",
    id: "17",
    code: "utg.",
    label: { nb: "utgiver", nn: "utgjevar" },
    marc21Code: "pbl",
    normarcCode: "utg.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "18",
    code: "ut\xF8v.",
    label: { nb: "ut\xF8ver", nn: "ut\xF8var" },
    marc21Code: "prf",
    normarcCode: "ut\xF8v.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "19",
    code: "arr.",
    label: { nb: "musikkarrang\xF8r", nn: "musikkarrang\xF8r" },
    marc21Code: "arr",
    normarcCode: "arr.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "20",
    code: "be\xE6ret p.",
    label: { nb: "be\xE6ret", nn: "\xE6ra" },
    marc21Code: "hnr",
    normarcCode: void 0,
    onixCodes: ["Z02"]
  },
  {
    type: "Role",
    id: "21",
    code: "intervjuer",
    label: { nb: "intervjuer", nn: "intervjuar" },
    marc21Code: "ivr",
    normarcCode: "forf.",
    onixCodes: ["A43"]
  },
  {
    type: "Role",
    id: "22",
    code: "kunstner",
    label: { nb: "kunstner", nn: "kunstnar" },
    marc21Code: "art",
    normarcCode: "illustr.",
    onixCodes: ["A07", "A35", "A39", "A46"]
  },
  {
    type: "Role",
    id: "23",
    code: "omsl.des.",
    label: { nb: "bokomslagsdesigner", nn: "bokomslagsdesignar" },
    marc21Code: "bjd",
    normarcCode: "illustr.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "24",
    code: "ideskaper",
    label: { nb: "id\xE9skaper", nn: "id\xE9skapar" },
    marc21Code: "ccp",
    normarcCode: "medarb.",
    onixCodes: ["A09", "A10"]
  },
  {
    type: "Role",
    id: "25",
    code: "fargel.",
    label: { nb: "fargelegger", nn: "fargeleggar" },
    marc21Code: "clr",
    normarcCode: "illustr.",
    onixCodes: ["A47"]
  },
  {
    type: "Role",
    id: "26",
    code: "utarb.",
    label: { nb: "utarbeider", nn: "utarbeidar" },
    marc21Code: "com",
    normarcCode: "red.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "27",
    code: "cop.inneh.",
    label: { nb: "copyrightinnehaver", nn: "copyrightinnehavar" },
    marc21Code: "cph",
    normarcCode: void 0,
    onixCodes: []
  },
  {
    type: "Role",
    id: "28",
    code: "konsulent",
    label: { nb: "konsulent", nn: "konsulent" },
    marc21Code: "csl",
    normarcCode: "medarb.",
    onixCodes: ["A51", "B20"]
  },
  {
    type: "Role",
    id: "29",
    code: "grad.ins.",
    label: { nb: "eksamenssted", nn: "eksamensstad" },
    marc21Code: "dgg",
    normarcCode: void 0,
    onixCodes: []
  },
  {
    type: "Role",
    id: "30",
    code: "saml.red.",
    label: { nb: "samlingsredakt\xF8r", nn: "samlingsredakt\xF8r" },
    marc21Code: "edc",
    normarcCode: "red.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "31",
    code: "vedt.jur.",
    label: { nb: "vedtakende jurisdiksjon", nn: "vedtakande jurisdiksjon" },
    marc21Code: "enj",
    normarcCode: void 0,
    onixCodes: []
  },
  {
    type: "Role",
    id: "32",
    code: "intervjuo.",
    label: { nb: "intervjuobjekt", nn: "intervjuobjekt" },
    marc21Code: "ive",
    normarcCode: "medarb.",
    onixCodes: ["A44"]
  },
  {
    type: "Role",
    id: "33",
    code: "sangforf.",
    label: { nb: "sangtekstforfatter", nn: "songtekstforfattar" },
    marc21Code: "lyr",
    normarcCode: "forf.",
    onixCodes: ["A04", "A05"]
  },
  {
    type: "Role",
    id: "34",
    code: "hoved-",
    label: { nb: "leder", nn: "leiar" },
    marc21Code: "led",
    normarcCode: void 0,
    onixCodes: []
  },
  {
    type: "Role",
    id: "35",
    code: "annen",
    label: { nb: "annen rolle", nn: "annan rolle" },
    marc21Code: "oth",
    normarcCode: "medarb.",
    onixCodes: ["A99", "Z99"]
  },
  {
    type: "Role",
    id: "36",
    code: "prosjektl.",
    label: { nb: "prosjektleder", nn: "prosjektleiar" },
    marc21Code: "pdr",
    normarcCode: "medarb.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "37",
    code: "sanger",
    label: { nb: "sanger", nn: "songar" },
    marc21Code: "sng",
    normarcCode: "ut\xF8v.",
    onixCodes: ["E05"]
  },
  {
    type: "Role",
    id: "38",
    code: "instr.",
    label: { nb: "instrumentalist", nn: "instrumentalist" },
    marc21Code: "itr",
    normarcCode: "ut\xF8v.",
    onixCodes: ["E06"]
  },
  {
    type: "Role",
    id: "39",
    code: "designer",
    label: { nb: "designer", nn: "designar" },
    marc21Code: "dsr",
    normarcCode: void 0,
    onixCodes: ["A11", "A52"]
  },
  {
    type: "Role",
    id: "40",
    code: "bokdesign.",
    label: { nb: "bokdesigner", nn: "bokdesignar" },
    marc21Code: "bkd",
    normarcCode: "medarb.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "41",
    code: "kurator",
    label: { nb: "kurator", nn: "kurator" },
    marc21Code: "cur",
    normarcCode: "kurator",
    onixCodes: []
  },
  {
    type: "Role",
    id: "42",
    code: "librettist",
    label: { nb: "librettist", nn: "librettist" },
    marc21Code: "lbt",
    normarcCode: "forf.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "43",
    code: "spillutv.",
    label: { nb: "spillutvikler", nn: "spillutviklar" },
    marc21Code: "gdv",
    normarcCode: void 0,
    onixCodes: []
  },
  {
    type: "Role",
    id: "44",
    code: "stemmesk.",
    label: { nb: "stemmeskuespiller", nn: "stemmeskodespelar" },
    marc21Code: "vac",
    normarcCode: "stemmesk.",
    onixCodes: []
  },
  {
    type: "Role",
    id: "45",
    code: "musiker",
    label: { nb: "musiker", nn: "musikar" },
    marc21Code: "mus",
    normarcCode: void 0,
    onixCodes: []
  }
];
var roleCodeList = roles.map((role) => role.code);
var isRoleCode = (roleCode) => roleCodeList.includes(roleCode);

// src/vocabularies/ageGroupList.ts
var ageGroupList = [
  {
    type: "AgeGroup",
    id: "1",
    code: "a",
    name: { nb: "3-5 \xE5r", nn: "3-5 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1001"
  },
  {
    type: "AgeGroup",
    id: "2",
    code: "b",
    name: { nb: "6-8 \xE5r", nn: "6-8 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1002"
  },
  {
    type: "AgeGroup",
    id: "3",
    code: "bu",
    name: { nb: "9-10 \xE5r", nn: "9-10 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1003"
  },
  {
    type: "AgeGroup",
    id: "4",
    code: "u",
    name: { nb: "11-12 \xE5r", nn: "11-12 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1004"
  },
  {
    type: "AgeGroup",
    id: "5",
    code: "mu",
    name: { nb: "13-15 \xE5r", nn: "13-15 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1005"
  },
  {
    type: "AgeGroup",
    id: "6",
    code: "aa",
    name: { nb: "0-2 \xE5r", nn: "0-2 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1000"
  },
  {
    type: "AgeGroup",
    id: "7",
    code: "vu",
    plasCode: "youngAdult",
    name: { nb: "16-17 \xE5r", nn: "16-17 \xE5r" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1015"
  },
  {
    type: "AgeGroup",
    id: "8",
    code: "v",
    marc21Code: "e",
    plasCode: "adult",
    name: { nb: "Voksne", nn: "Vaksne" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1016"
  },
  {
    type: "AgeGroup",
    id: "9",
    code: "j",
    marc21Code: "j",
    plasCode: "child",
    name: { nb: "Barn og ungdom", nn: "Barn og ungdom" },
    nortargetUri: void 0
  }
];

// src/vocabularies/bindingTypeList.ts
var bindingTypeList = [
  {
    type: "BindingType",
    id: "1",
    code: "ikke oppgitt",
    name: { nb: "informasjon ikke oppgitt", nn: "informasjon ikkje oppgitt" },
    onixCodes: []
  },
  {
    type: "BindingType",
    id: "4",
    code: "spiralrygg",
    name: { nb: "spiralrygg", nn: "spiralrygg" },
    onixCodes: ["BE"]
  },
  {
    type: "BindingType",
    id: "5",
    code: "ringperm",
    name: { nb: "ringperm", nn: "ringperm" },
    onixCodes: []
  },
  {
    type: "BindingType",
    id: "6",
    code: "heftet",
    name: { nb: "heftet", nn: "hefta" },
    onixCodes: ["BC"]
  },
  {
    type: "BindingType",
    id: "7",
    code: "innbundet",
    name: { nb: "innbundet", nn: "innbunden" },
    onixCodes: ["BB"]
  },
  {
    type: "BindingType",
    id: "13",
    code: "kartonert",
    name: { nb: "kartonert", nn: "kartonert" },
    onixCodes: ["BH"]
  },
  {
    type: "BindingType",
    id: "14",
    code: "fleksibind",
    name: { nb: "fleksibind", nn: "fleksibind" },
    onixCodes: ["BL"]
  }
];

// src/vocabularies/accessibleContentFeatureList.ts
var accessibleContentFeatureList = [
  {
    type: "AccessibleContentFeature",
    id: "1",
    code: "ta",
    label: { nb: "Lettlest", nn: "Lettlese" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1006"
  },
  {
    type: "AccessibleContentFeature",
    id: "2",
    code: "tb",
    label: { nb: "Tilrettelagt enkelt innhold", nn: "Tilrettelagt enkelt innhald" },
    nortargetUri: "https://schema.nb.no/Bibliographic/Values/TG1007"
  }
];

// src/utils/formatIsbdTitle.ts
var formatIsbdTitle = (data, options = {}) => {
  const { title, parallelTitles = [] } = data;
  if (title.titleType === "uniform_title_music") {
    return formatUniformTitleMusic(title);
  } else {
    return [title, ...parallelTitles].map((title2) => formatSingleTitle(title2, options)).join(" = ");
  }
};
var formatSingleTitle = ({ mainTitle, subtitle, partNumber, partTitle }, options) => mainTitle + (subtitle && !options.skipSubtitle ? ` : ${subtitle}` : "") + (partNumber ? `. ${partNumber}` : "") + (partTitle ? `. ${partTitle}` : "");
var formatUniformTitleMusic = ({
  form,
  instrumentation,
  serialNumber,
  opusNumber,
  thematicCatalogueNumber,
  part,
  key,
  arrangement
}) => form + (instrumentation ? ` ${instrumentation}` : "") + (serialNumber ? `, ${serialNumber}` : "") + (opusNumber ? `, ${opusNumber}` : "") + (thematicCatalogueNumber ? `, ${thematicCatalogueNumber}` : "") + (part ? `, ${part}` : "") + (key ? `, ${key}` : "") + (arrangement ? `, ${arrangement}` : "");

// src/utils/formatPersonLabel.ts
var translateLocalizedString = (localizedString, languageCode = "nb") => localizedString?.[languageCode] ?? localizedString?.nb;
var getSignificantYear = (birth, death, activeYears) => {
  if (birth && death) return `${getYearLabel(birth)} - ${getYearLabel(death)}`;
  if (birth) return `${getYearLabel(birth)}-`;
  if (death) return `-${getYearLabel(death)}`;
  if (activeYears) return `${getYearLabel(activeYears)}`;
  return "";
};
var getYearLabel = (personYear) => {
  let label = personYear.year.toString();
  if (personYear.isApproximate) label = `ca. ${label}`;
  if (personYear.year < 0) label = `${label.replace("-", "")} f.Kr. `;
  return label;
};
var formatPersonLabel = ({ id, name, ordinal, personTitle, fieldOfActivity, birth, death, activeYears, unspecifiedDate }, {
  hide = []
  // eslint-disable-next-line @typescript-eslint/ban-types
} = {}) => [
  translateLocalizedString(name) ?? id,
  ordinal,
  translateLocalizedString(personTitle),
  !hide.includes("fieldOfActivity") && fieldOfActivity ? ` (${translateLocalizedString(fieldOfActivity)})` : "",
  !hide.includes("birth") && !hide.includes("death") && !hide.includes("activeYears") && getSignificantYear(birth, death, activeYears),
  !hide.includes("unspecifiedDate") && unspecifiedDate
].filter(Boolean).join(" ");

// src/utils/getContributorWemiLevelFromRoleCode.ts
var contributorRoleWemiLevelMap = {
  "bearb.": ({ isMainContributor }) => isMainContributor ? "Work" : "Expression",
  "dir.": "Expression",
  "forf.": "Work",
  "fotogr.": "Manifestation",
  "illustr.": "Manifestation",
  "innl.": "Expression",
  //"komm.": "???", // To be decided
  // MR65: Komponister legges som hovedregel på Verk, men i tilfeller hvor komponister dukker opp for språkbaserte verk
  // legger vi dem på Uttrykk fordi vi antar at det da er snakk om komposisjon for lydbokuttrykk. Eksempel: Heljar Hagen
  // som har komponert musikk for *Georgs magiske medisin* (1208349).
  "komp.": (ctx) => {
    if (ctx.version === "1") {
      return ctx.workTypes.length && isLanguageBasedWork({ workTypes: ctx.workTypes }) ? "Expression" : "Work";
    } else {
      return ctx.workTypeCode === "literary" ? "Expression" : "Work";
    }
  },
  "manusforf.": "Work",
  "medarb.": "Manifestation",
  // Dette er en sånn "alle andre"-rolle som kan bety hva som helst
  "medforf.": "Work",
  "oppr.forf.": "Work",
  // Skal ligge på verket som dette verket er basert på. Kan legges på verk midlertidig
  "overs.": "Expression",
  // MR53: Hvis det finnes en hovedansvarshaver, skal redaktør ligge på manifestasjon, ellers på verk.
  "red.": ({ hasMainContributor }) => hasMainContributor ? "Manifestation" : "Work",
  "regiss\xF8r": "Work",
  "skuesp.": "Work",
  "utg.": "Manifestation",
  "ut\xF8v.": ({ isMainContributor }) => isMainContributor ? "Work" : "Expression",
  "arr.": "Expression",
  "be\xE6ret p.": "Work",
  "intervjuer": "Work",
  // MR67: For verk der verkstype != bildeverk skal kunstner ikke ligge på verk men legges på og være skillende element for uttrykk
  "kunstner": (ctx) => {
    if (ctx.version === "1") {
      return isImageWork({ workTypes: ctx.workTypes }) ? "Work" : "Expression";
    } else {
      return ctx.workTypeCode === "image" ? "Work" : "Expression";
    }
  },
  "omsl.des.": "Manifestation",
  "ideskaper": "Work",
  // Skal ligge på verket som dette verket er basert på. Kan legges på verk midlertidig
  "fargel.": "Expression",
  "utarb.": "Work",
  "cop.inneh.": "Work",
  "konsulent": "Work",
  "grad.ins.": "Work",
  // Ikke brukt
  "saml.red.": "Manifestation",
  // Antyder nærværet av en samlingsredaktør at vi har et aggregatverk som er sterkt nok til å registreres som eget verk? I så fall opp på verk.
  "vedt.jur.": "Work",
  "intervjuo.": "Work",
  "sangforf.": "Work",
  "hoved-": "Work",
  // Kan kun brukes sammen med en annen rolle og skal følge med den rollen (Hva betyr det??).
  "annen": "Manifestation",
  "prosjektl.": "Work",
  "sanger": "Expression",
  // Eller verk?
  "instr.": "Expression",
  // Eller verk?
  "designer": "Work",
  "bokdesign.": "Manifestation",
  "kurator": "Work",
  "librettist": "Work",
  "spillutv.": "Work",
  "musiker": "Work",
  "komm.": "Expression",
  "stemmesk.": "Expression"
};
var getContributorWemiLevelFromRoleCode = (roleCode, context, defaultLevel = "Manifestation") => {
  if (!isRoleCode(roleCode))
    return { level: defaultLevel, warnings: [{ code: "TODO", info: `Ugyldig rollekode: \xAB${roleCode}\xBB` }] };
  const contributorLevel = contributorRoleWemiLevelMap[roleCode];
  return { level: typeof contributorLevel === "function" ? contributorLevel(context) : contributorLevel, warnings: [] };
};
var yearRangeMatcher = /(\d+)-(\d+)/;
var parseYearRange = (value) => {
  const match = yearRangeMatcher.exec(value);
  if (!match || !match[1] || !match[2]) return;
  const parsed = z.object({ startYear: z.coerce.number(), endYear: z.coerce.number() }).safeParse({ startYear: parseInt(match[1]), endYear: parseInt(match[2]) });
  if (parsed.success) return parsed.data;
};
var parseAgeRanges = (ageGroups) => {
  return ageGroups.map((ageGroup) => parseYearRange(ageGroup.name.nb)).filter((ageGroup) => !!ageGroup);
};
var getAgeRangeFromAgeGroups = (ageGroups) => parseAgeRanges(ageGroups).reduce(
  (acc, cur) => ({
    startYear: acc && cur.startYear < acc.startYear ? cur.startYear : acc?.startYear ?? cur.startYear,
    endYear: acc && cur.endYear > acc.endYear ? cur.endYear : acc?.endYear ?? cur.endYear
  }),
  void 0
);

export { AccessibleContentFeatureSchema, AgeGroupSchema, AgeLimitSchema, AgentSchema, BindingTypeSchema, CollectionExpressionEntrySchema, CollectionExpressionSchema, CollectionWorkSchema, CollectiveAgentSchema, ConceptSchema, ContentsEntrySchema, ContributorSchema, CordataChangeEventSchema, CordataEntitySchema, CordataEntityV2Schema, CordataTransformWarningSchema, DocumentTypeSchema, DurationRecordSchema, ExpressionSchema, GenreSchema, ImprintSchema, LanguageMapListSchema, LanguageMapListWithEnglishSchema, LanguageMapSchema, LanguageMapWithEnglishSchema, LanguageSchema, LibraryCatalogueRecordReferenceSchema, ManifestationSchema, Nationality, NoteSchema, NumberOfPlayersSchema, PersonSchema, PlaceSchema, PlaceType, PlatformSchema, PublicationStatementSchema, RoleSchema, SubjectSchema, TitleSchema, UniformTitleMusicSchema, WorkSchema, WorkTypeSchema, YearSchema, accessibleContentFeatureList, ageGroupList, bindingTypeList, cartographicWork, cordataTransformWarnings, countries, defaultWorkType, documentTypeList, expressionTypeFromCode, expressionTypeSchema, expressionTypes, formatIsbdTitle, formatPersonLabel, getAgeRangeFromAgeGroups, getContributorWemiLevelFromRoleCode, getDocumentTypeFormatCode, getDocumentTypeFromCode, getExpressionTypeLabel, getYearLabel, imageWork, instrumentationSchema, interactiveWork, isCartographicWork, isCordataLanguageCode, isDocumentTypeCode, isExpressionTypeCode, isFromPromus, isImageWork, isInstrumentation, isInteractiveWork, isLanguageBasedWork, isLibrarySystem, isMovingImageWork, isMusicInstrument, isMusicalWork, isNtsfId, isPlatformCode, isRoleCode, isWorkTypeId, languageBasedWork, languageNameMap, librarySystems, movieGenreList, movingImageWork, musicInstrumentSchema, musicalWork, nmaAgeLimitList, nmbfIdNamespace, originLabelMap, originNames, originSchema, platformList, promusIdAuthorityNamespace, promusIdItemNamespace, roleCodeList, roles, translateDocumentTypeFormat, translateLocalizedString, version, workTypeIds, workTypes };
